/**
 * Import react libraries.
 */
import * as React from 'react';

/**
 * Import third-party libraries.
 */
import styled from 'styled-components';
import DatePicker from 'react-datepicker';
import en from 'date-fns/locale/en-GB';
import moment from "moment";

/**
 * Import custom components.
 */
import COLORS from '../styles/colors';
import { Calendar, Attention } from '../icons/index';
import { UIIconButton } from '../Button/index';

/**
 * Import custom styles.
 */
import { InputFieldStyling, InputWrapper, RequiredStar, ErrorMessage } from './styling';
import "react-datepicker/dist/react-datepicker.css"

/**
 * Add custom types.
 */
type DatepickerFieldProps = {
  id: string;
  disabled?: boolean;
  onChange?: (date: Date) => void;
  hasError?: boolean;
  value?: Date;
  dateFormat?: string;
  validationMessage?: string;
  autoComplete?: string;
  placeholder?: string;
  required?: boolean;
};

/**
 * Add custom styles.
 */
const DatePickerContainer = styled.div`
  > div {
    display: block;

    .react-datepicker__navigation {
      line-height: normal;
      text-indent: inherit;
      border: none;
      border-radius: 4px;
      height: 32px;
      width: 32px;
      outline: none;
      margin: 0px;
      color: transparent;

      &:hover {
        background: ${COLORS.primary_20};

        &.react-datepicker__navigation--previous::before {
          background-image: url("data:image/svg+xml,%3Csvg style='width: 32px; height: 32px;' viewBox='0 0 25 25' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fillRule='evenodd' clipRule='evenodd' d='M15.1975 4.46474C15.5885 4.85478 15.5893 5.48794 15.1993 5.87895L8.92124 12.1727L15.1993 18.4665C15.5893 18.8575 15.5885 19.4907 15.1975 19.8807C14.8065 20.2708 14.1734 20.27 13.7833 19.879L6.8008 12.879C6.41145 12.4886 6.41145 11.8568 6.8008 11.4665L13.7833 4.46651C14.1734 4.0755 14.8065 4.0747 15.1975 4.46474Z' fill='%23215369' /%3E%3C/svg%3E");
        }

        &.react-datepicker__navigation--next::before {
          background-image: url("data:image/svg+xml,%3Csvg style='width: 32px; height: 32px;' viewBox='0 0 25 25' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fillRule='evenodd' clipRule='evenodd' d='M9.24214 19.8807C8.85113 19.4907 8.85034 18.8575 9.24038 18.4665L15.5184 12.1727L9.24037 5.87895C8.85034 5.48794 8.85113 4.85478 9.24214 4.46474C9.63315 4.0747 10.2663 4.07549 10.6564 4.46651L17.6389 11.4665C18.0282 11.8568 18.0282 12.4886 17.6389 12.879L10.6564 19.879C10.2663 20.27 9.63316 20.2708 9.24214 19.8807Z' fill='%23215369' /%3E%3C/svg%3E");
        }

      }

      &:focus {
        background: ${COLORS.primary_100};
        box-shadow: 0px 4px 12px ${COLORS.focus_25}, 0px 0px 8px ${COLORS.focus} !important;

        &.react-datepicker__navigation--next::before {
          background-image: url("data:image/svg+xml,%3Csvg style='width: 32px; height: 32px;' viewBox='0 0 25 25' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fillRule='evenodd' clipRule='evenodd' d='M9.24214 19.8807C8.85113 19.4907 8.85034 18.8575 9.24038 18.4665L15.5184 12.1727L9.24037 5.87895C8.85034 5.48794 8.85113 4.85478 9.24214 4.46474C9.63315 4.0747 10.2663 4.07549 10.6564 4.46651L17.6389 11.4665C18.0282 11.8568 18.0282 12.4886 17.6389 12.879L10.6564 19.879C10.2663 20.27 9.63316 20.2708 9.24214 19.8807Z' fill='%23163746' /%3E%3C/svg%3E");
        }

        &.react-datepicker__navigation--previous::before {
          background-image: url("data:image/svg+xml,%3Csvg style='width: 32px; height: 32px;' viewBox='0 0 25 25' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fillRule='evenodd' clipRule='evenodd' d='M15.1975 4.46474C15.5885 4.85478 15.5893 5.48794 15.1993 5.87895L8.92124 12.1727L15.1993 18.4665C15.5893 18.8575 15.5885 19.4907 15.1975 19.8807C14.8065 20.2708 14.1734 20.27 13.7833 19.879L6.8008 12.879C6.41145 12.4886 6.41145 11.8568 6.8008 11.4665L13.7833 4.46651C14.1734 4.0755 14.8065 4.0747 15.1975 4.46474Z' fill='%23163746' /%3E%3C/svg%3E");
        }
      }

      &::before {
        text-align: center;
        display: inline-block;
        content: "";
        height: 32px;
        width: 32px;
        vertical-align: middle;
      }

      &.react-datepicker__navigation--next::before {
        background-image: url("data:image/svg+xml,%3Csvg style='width: 32px; height: 32px;' viewBox='0 0 25 25' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fillRule='evenodd' clipRule='evenodd' d='M9.24214 19.8807C8.85113 19.4907 8.85034 18.8575 9.24038 18.4665L15.5184 12.1727L9.24037 5.87895C8.85034 5.48794 8.85113 4.85478 9.24214 4.46474C9.63315 4.0747 10.2663 4.07549 10.6564 4.46651L17.6389 11.4665C18.0282 11.8568 18.0282 12.4886 17.6389 12.879L10.6564 19.879C10.2663 20.27 9.63316 20.2708 9.24214 19.8807Z' fill='%23666666' /%3E%3C/svg%3E");
      }

      &.react-datepicker__navigation--previous::before {
        background-image: url("data:image/svg+xml,%3Csvg style='width: 32px; height: 32px;' viewBox='0 0 25 25' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fillRule='evenodd' clipRule='evenodd' d='M15.1975 4.46474C15.5885 4.85478 15.5893 5.48794 15.1993 5.87895L8.92124 12.1727L15.1993 18.4665C15.5893 18.8575 15.5885 19.4907 15.1975 19.8807C14.8065 20.2708 14.1734 20.27 13.7833 19.879L6.8008 12.879C6.41145 12.4886 6.41145 11.8568 6.8008 11.4665L13.7833 4.46651C14.1734 4.0755 14.8065 4.0747 15.1975 4.46474Z' fill='%23666666' /%3E%3C/svg%3E");
      }
    }

    .react-datepicker__month-container {
      width: 336px;
    }

    .react-datepicker__month {
      margin: 0px;
    }

    .react-datepicker-popper[data-placement^="bottom"] .react-datepicker__triangle {
      border-bottom-color: ${COLORS.neutral_20}
    }
    
    .react-datepicker__header {
      border-bottom: 1px solid ${COLORS.neutral_200};
      background: ${COLORS.neutral_20};
      height: 96px;
      padding-top: 0px;
    }

    .react-datepicker__current-month {
      color: ${COLORS.neutral_600};
      font-weight: 700;
      height: 48px;
      display: inline-flex;
      align-items: center;
    }

    .react-datepicker__day-name {
      color: ${COLORS.neutral_600};
      font-weight: 400;
      display: inline-flex;
      justify-content: center;
      align-items: center;
      height: 48px;
      margin: 0px;
      width: 48px;
    }

    .react-datepicker__day {
      color: ${COLORS.neutral_600};
      background: ${COLORS.white};
      font-weight: 400;
      border: none;
      display: inline-flex;
      justify-content: center;
      align-items: center;
      height: 44px;
      width: 44px;
      margin: 2px;

      &.react-datepicker__day--keyboard-selected {
        color: ${COLORS.neutral_600};
        background: ${COLORS.white};
        font-weight: 400;
        border: none;
      }

      &:hover {
        color: ${COLORS.primary_700};
        background: ${COLORS.primary_20};
      }

      &:focus {
        color: ${COLORS.primary_800};
        background: ${COLORS.primary_100};
        box-shadow: 0px 4px 12px ${COLORS.focus_25}, 0px 0px 8px ${COLORS.focus} !important;
        outline: none;
      }

      &.react-datepicker__day--selected {
        color: ${COLORS.white} !important;
        background: ${COLORS.primary_500} !important;
      }

      &.react-datepicker__day--today {
        border-radius: 0.3rem;
        color: ${COLORS.neutral_700};
        background: ${COLORS.neutral_100};

        &:hover {
          color: ${COLORS.primary_700};
          background: ${COLORS.primary_20};
        }
  
        &:focus {
          color: ${COLORS.primary_800};
          background: ${COLORS.primary_100};
          box-shadow: 0px 4px 12px ${COLORS.focus_25}, 0px 0px 8px ${COLORS.focus} !important;
          outline: none;
        }
  
      }
    }
  }
`;

const DatepickerRow = styled.div`
  display: flex;
  flex-direction: row;
  position: relative;

  > button {
    position: absolute;
    right: 4px;
    top: 4px;

    svg {
      padding-top: 9px;
    }
  }
`;

const DatepickerField = ({ id, disabled, onChange, hasError, value, validationMessage, dateFormat, autoComplete, placeholder, required }: DatepickerFieldProps) => {
  // Globally used variables within the component
  const inputRef = React.useRef<any>(null);
  const [tabbedHere, setTabbedHere] = React.useState<boolean>(false);

  /**
   * Format the date in a specific way.
   */
  React.useEffect(() => {
    if (value) {
      inputRef.current.value = moment(value).format(dateFormat ? dateFormat : "MMMM Do, YYYY");
    }
  }, [value]);

  return (
    <>
      { /* Let's render the required star */ }
      {required && <RequiredStar>*</RequiredStar>}

      { /* Let's render the input itself */ }
      <DatePickerContainer>
        <DatePicker
          onChange={(e: any) => onChange && onChange(e || '')}
          disabled={disabled}
          locale={en}
          selected={value ? value : undefined}
          customInput={
            <InputWrapper
              disabled={disabled}
              tabIndex={disabled ? -1 : 0}
              onFocus={(e: any) => {
                if (!e?.target?.id.includes(id) && !disabled) {
                  setTabbedHere(true);
                  inputRef.current.focus();
                }
              }}>
              <div style={{ display: 'block' }}>
                <DatepickerRow>
                  <InputFieldStyling
                    id={id}
                    ref={inputRef}
                    type="text"
                    name="datepicker"
                    readOnly={true}
                    className={hasError ? 'error' : ''}
                    tabIndex={-1}
                    autoComplete={autoComplete}
                    placeholder={placeholder}
                    disabled={disabled}
                    required={required}
                    tabbedHere={tabbedHere}
                    onBlur={() => setTabbedHere(false)}
                  />
                  <UIIconButton action={() => {}} disabled={disabled}>
                    <Calendar size="16" color={COLORS.neutral_600} />
                  </UIIconButton>
                </DatepickerRow>
              </div>
            </InputWrapper>
          } />
      </DatePickerContainer>

      { /* If there is an error, let's render the error */ }
      {validationMessage && 
        <ErrorMessage>
          <Attention size="20px" color={COLORS.critical_400} />
          <span>{validationMessage}</span>
        </ErrorMessage>
      }
    </>
  );
};

export default DatepickerField;
